<template>
  <div
    ref="pageWrapperRef"
    class="relative"
    :class="{
      'w-full h-full flex flex-col flex-auto items-center justify-center': isScaleContentEnabled,
    }"
  >
    <div
      v-if="isScaleContentEnabled"
      ref="pageContainerRef"
      class="overflow-hidden flex flex-col max-w-full max-h-full justify-center"
      :class="
        !hasFluidUnlockedSection ? [isHorizontalAspectRatio ? 'w-full h-auto' : 'h-full w-auto'] : 'w-full h-full'
      "
      :style="!hasFluidUnlockedSection ? aspectRatioStyle : undefined"
    >
      <div
        ref="pageContentRef"
        class="flex flex-col overflow-visible"
        :style="
          scaleFactor < 1 &&
          !Array.from(visibleCollectionPlayerRefs).some((player) => player?.value?.isFullscreen?.value) &&
          !hasFluidUnlockedSection
            ? { scale: scaleFactor, transformOrigin: 'center center' }
            : {}
        "
      >
        <slot />
      </div>
    </div>

    <div v-else :class="{ 'flex flex-col': isDefaultDisplayMode }">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type CSSProperties } from 'vue'
import { useElementSize } from '@vueuse/core'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import useElementSizeAfterDom from '@lib/composables/useElementSizeAfterDom.ts'
import useCanvas from '@canvas-builder/composables/useCanvas.ts'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'

interface Props {
  isScaleContentEnabled?: boolean
  aspectRatioStyle: CSSProperties | undefined
  aspectRatio: { x: number; y: number } | undefined
}

const props = defineProps<Props>()

const { isDefaultDisplayMode, visibleCollectionPlayerRefs } = useCanvas()
const { activeSection, canvasPages, currentPageIndex } = useCanvasPages()

const pageWrapperRef = ref<HTMLElement | null>(null)
const pageContainerRef = ref<HTMLElement | null>(null)
const pageContentRef = ref<HTMLElement | null>(null)

const containOnlyScribble = computed(() => {
  const components = activeSection.value?.content?.data
  const currentPage = canvasPages.value?.[currentPageIndex.value]?.[0]

  return (
    (components?.length === 1 && components[0]?.type === ComponentTypes.Scribble) ||
    currentPage?.type === ComponentTypes.Scribble
  )
})

const { width: pageContainerWidth, height: pageContainerHeight } = useElementSize(pageContainerRef)
const { width: pageWrapperWidth, height: pageWrapperHeight } = useElementSize(pageWrapperRef)
const { clientWidth: contentClientWidth, clientHeight: contentClientHeight } = useElementSizeAfterDom(pageContentRef)

const isHorizontalAspectRatio = computed(() => {
  if (!props.aspectRatio) return true

  if (containOnlyScribble.value) {
    return props.aspectRatio.x > props.aspectRatio.y
  }

  const x = pageWrapperWidth.value / props.aspectRatio.x
  const y = pageWrapperHeight.value / props.aspectRatio.y

  return x < y
})

const hasFluidUnlockedSection = computed(() => {
  // Check if the active section has fluid mode enabled with aspect ratio unlocked
  return activeSection.value?.theme?.fluid === true && activeSection.value?.theme?.aspect_ratio === 'unlocked'
})

const scaleFactor = computed(() => {
  const xScale = pageContainerWidth.value / contentClientWidth.value || 1
  const yScale = pageContainerHeight.value / contentClientHeight.value || 1

  return Math.min(xScale, yScale, 1)
})

defineExpose({
  pageWrapperRef,
  pageContainerRef,
  pageContentRef,
})
</script>
